<template>
	<tbody v-if="currentPageContacts" class="table-body" data-test="table-body">
		<tr
			v-for="(contact, index) in currentPageContacts"
			class="table-body-row"
			data-test="table-body-row"
			:class="{ 'show-owner': canPerformProfileSelector }"
			:key="index"
		>
			<td class="table-body__checkbox">
				<CheckBox
					:checked="isSelectedContact(contact.leadId)"
					:aria-label="`Select the contact for ${contact.firstName} ${contact.lastName}`"
					@onClick="
						handleContactSelection(contact.leadId, ...arguments)
					"
				/>
			</td>
			<td
				class="table-body__name b360-handle-overflow"
				:data-tour="index === 0 ? 'cc-contact-name' : ''"
			>
				<ContactIcon
					class="contact-icon"
					:disabled="!activeContacts"
					:firstName="contact.firstName"
					:lastName="contact.lastName"
				/>
				<TextButton
					aria-label="Full Name"
					:disabled="!activeContacts"
					:title="
						truncateModalFields({
							fieldName: `${contact.firstName} ${contact.lastName}`,
							fieldLength: 20,
							attributeName: 'title',
						})
					"
					@onClick="handleContactClick(contact)"
				>
					{{
						truncateModalFields({
							fieldName: `${contact.firstName} ${contact.lastName}`,
							fieldLength: 20,
						})
					}}
				</TextButton>
			</td>
			<td class="table-body__email b360-handle-overflow">
				<a
					class="link"
					data-test="email"
					role="button"
					:aria-label="`Send an e-mail to ${contact.email}`"
					:class="{ disabled: !activeContacts }"
					:title="
						truncateModalFields({
							fieldName: contact.email,
							fieldLength: 30,
							attributeName: 'title',
						})
					"
					:href="`mailto:${contact.email}`"
				>
					{{
						truncateModalFields({
							fieldName: contact.email,
							fieldLength: 30,
						})
					}}
				</a>
			</td>
			<td class="table-body__phone">
				<a
					class="link"
					data-test="phone"
					role="button"
					:aria-label="`Call ${formattedPhoneNumber(contact.phone)}`"
					:class="{ disabled: !activeContacts }"
					:href="`tel:${formattedPhoneNumber(contact.phone)}`"
				>
					{{ formattedPhoneNumber(contact.phone) }}
				</a>
			</td>
			<td
				class="table-body__type"
				data-test="contact-type"
				:class="{ disabled: !activeContacts }"
			>
				<span>{{ contact.type }}</span>
				<span class="business-name" data-test="business-name">{{
					contact.businessName
				}}</span>
			</td>
			<td
				v-if="canPerformProfileSelector"
				class="table-body__owner"
				data-test="owner"
				:class="{ disabled: !activeContacts }"
			>
				{{
					truncateModalFields({
						fieldName: contact.owner,
						fieldLength: 30,
					})
				}}
			</td>
			<td class="table-body__quick-actions">
				<QuickActionsMenu
					data-test="contact-quick-actions-menu"
					data-tour="cc-single-quick-actions"
					:lightButton="true"
					:lightButtonIcon="['far', 'ellipsis-h']"
					:menus="populateSingleQuickActions(contact.contactId)"
					@sendToBlink="handleBlinkClick(contact)"
					@sendToVendor="showVendorModal(contact)"
					@changeContactTypeModal="handleChangeContactTypeModal(contact)"
					@showTransferModal="handleShowTransferModal(contact)"
					@showDeactivateModal="handleShowDeactivateModal(contact)"
					@showReactivateModal="handleShowReactivateModal(contact)"
					@showEditModal="handleShowEditModal(contact)"
					@addLeadNote="handleAddLeadNote(contact)"
					@showUConnect="handleShowUConnect(contact)"
				/>
			</td>
		</tr>
	</tbody>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { truncateModalFields } from '../helpers';
import { VIEWS as MODAL_VIEWS } from '../modals/constants';
import leadsService from '@/services/contacts/leads-service.js';

import CheckBox from '../CheckBox.vue';
import ContactIcon from '../ContactIcon.vue';
import QuickActionsMenu from '../QuickActionsMenu.vue';
import TextButton from '@/components/global/TextButton.vue';

export default {
	name: 'ContactTableBody',
	components: {
		CheckBox,
		ContactIcon,
		QuickActionsMenu,
		TextButton,
	},
	props: {
		activeContacts: Boolean,
		currentPageContacts: Array,
		selectAllMode: Boolean,
	},
	computed: {
		...mapGetters([
			'canDeleteContact',
			'canReassignLeadsToDifferentContact',
			'isImpersonated',
		]),
		...mapState({
			// ccContacts
			selectedLeadIds: (state) => state.ccContacts.leads.selectedLeadIds,
			// profile
			showVendorLinkQuickAction: (state) => !!state.profile.bigMacLink,
			// profile switcher
			canPerformProfileSelector: (state) =>
				state.profileSwitcher.mainProfileSelector
					.canPerformProfileSelector,
			loggedInUserId: (state) =>
				state.profileSwitcher.mainProfile.rolodexContactId,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
		}),
	},
	methods: {
		...mapActions([
			'handleAddBlinkData',
			'setLeadIdClicked',
			'setModalHistory',
			'setModalPreference',
			'setModalView',
			'setSelectedLeadIds',
			'setShowOverviewModal',
		]),
		truncateModalFields,
		isSelectedContact(leadId) {
			if (this.selectAllMode) {
				return !this.selectedLeadIds.includes(leadId);
			} else {
				return this.selectedLeadIds.includes(leadId);
			}
		},
		handleContactSelection(leadId, selected) {
			if (this.selectAllMode === selected) {
				this.setSelectedLeadIds(
					this.selectedLeadIds.filter((id) => id !== leadId),
				);
			} else {
				this.setSelectedLeadIds([...this.selectedLeadIds, leadId]);
			}
		},
		formattedPhoneNumber(phoneNumber) {
			let formattedNumber = '';
			if (phoneNumber === null || phoneNumber === '') {
				formattedNumber = null;
			} else {
				const newPhoneNumber = phoneNumber.replace(/\D/g, '');
				formattedNumber = `(${newPhoneNumber.slice(
					0,
					3,
				)}) ${newPhoneNumber.slice(3, 6)}-${newPhoneNumber.slice(
					6,
					10,
				)}`;
			}
			return formattedNumber;
		},
		populateSingleQuickActions(ownerId) {
			if (!this.activeContacts) {
				return [
					[
						{
							icon: ['far', 'recycle'],
							iconColor: 'green',
							text: 'Reactivate Contact',
							action: 'showReactivateModal',
							disabled:
								this.mainProfileIsInternalUser ||
								!this.canDeleteContact,
						},
					],
				];
			}
			const menu = [
				[
					{
						icon: ['far', 'pen'],
						text: 'Edit Contact',
						action: 'showEditModal',
					},
					{
						icon: ['fas', 'user-cog'],
						text: 'Change Contact Type',
						action: 'changeContactTypeModal',
						disabled:
							this.mainProfileIsInternalUser ||
							!this.canReassignLeadsToDifferentContact,
					},
					{
						icon: ['far', 'exchange'],
						text: 'Transfer Contact',
						action: 'showTransferModal',
						disabled:
							this.mainProfileIsInternalUser ||
							!this.canReassignLeadsToDifferentContact,
					},
					{
						icon: ['far', 'file-import'],
						text: 'Send to Blink+',
						action: 'sendToBlink',
						disabled:
							this.mainProfileIsInternalUser ||
							this.isImpersonated ||
							this.loggedInUserId !== ownerId,
					},
					{
						icon: ['far', 'sticky-note'],
						text: 'Add Note',
						action: 'addLeadNote',
						disabled: this.mainProfileIsInternalUser,
					},
				],
				[
					{
						icon: ['fa', 'bell-slash'],
						text: 'Suppress UConnect Alerts',
						action: 'showUConnect',
						disabled: this.mainProfileIsInternalUser,
					},
				],
				[
					{
						icon: ['far', 'trash-alt'],
						iconColor: 'red',
						text: 'Mark Contact Inactive',
						action: 'showDeactivateModal',
						disabled:
							this.mainProfileIsInternalUser ||
							!this.canDeleteContact,
					},
				],
			];

			if (this.showVendorLinkQuickAction) {
				menu[0].push({
					icon: ['far', 'file-import'],
					text: 'Send Vendor Link',
					action: 'sendToVendor',
					disabled:
						this.mainProfileIsInternalUser || this.isImpersonated,
				});
			}

			return menu;
		},
		handleContactClick(lead) {
			this.openModal(lead, MODAL_VIEWS.HOME);
			this.openModal(lead, MODAL_VIEWS.HOME);
		},
		handleBlinkClick(lead) {
			this.openModal(lead, MODAL_VIEWS.SEND_TO_BLINK);
		},
		showVendorModal(lead) {
			this.openModal(lead, MODAL_VIEWS.SEND_TO_VENDOR);
		},
		handleChangeContactTypeModal(lead) {
			this.openModal(lead, MODAL_VIEWS.UPDATE_CONTACT_TYPE);
		},
		handleShowTransferModal(lead) {
			this.openModal(lead, MODAL_VIEWS.TRANSFER_CONTACT);
		},
		handleShowDeactivateModal(lead) {
			this.openModal(lead, MODAL_VIEWS.DEACTIVATE_CONTACT);
		},
		handleShowReactivateModal(lead) {
			this.openModal(lead, MODAL_VIEWS.REACTIVATE_CONTACT);
		},
		handleShowEditModal(lead) {
			this.openModal(lead, MODAL_VIEWS.CONTACT_DETAILS);
		},
		handleAddLeadNote(lead) {
			this.openModal(lead, MODAL_VIEWS.ADD_NOTE);
		},
		async handleShowUConnect(lead) {
			const response = await leadsService.getPreferencesByLeadId(
				lead.leadId,
			);
			const UConnectAlertPref = response.preferences.find(
				(pref) => pref.typeCode === 'MarketWatch',
			);
			const UConnect = {
				alertPreference: UConnectAlertPref,
				loanId: null,
				leadId: lead.leadId,
				view: MODAL_VIEWS.ALERT_SETTINGS,
			};
			this.setModalHistory([MODAL_VIEWS.CONTACT_SETTINGS]);
			this.setModalPreference(UConnect);
			this.openModal(lead, MODAL_VIEWS.ALERT_SETTINGS);
		},
		openModal(lead, view) {
			this.handleAddBlinkData(lead);
			this.setLeadIdClicked(lead.leadId);
			this.setModalView(view);
			this.setShowOverviewModal(true);
		},
	},
};
</script>

<style scoped lang="scss">
.table-body__name {
	display: flex;

	.contact-icon {
		@include rem('margin-right', 15px);
		flex-shrink: 0;
	}
}

.table-body__type {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	gap: 2px;

	.business-name {
		font-size: 14px;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}
}

.table-body__quick-actions {
	display: flex;
	justify-content: flex-end;
}

.table-body__quick-actions /deep/ .btn-quick-actions {
	@include rem('width', 34px);
	@include rem('height', 34px);
}

.table-body-row {
	@include rem('padding', 2.5px 20px 2.5px 20px);
	border-top: 1px solid $border-color-light;
	display: grid;
	grid-template-columns: 4% repeat(2, 1fr) repeat(2, 12%) 16% 44px;
	gap: 15px;
	align-items: center;
	&.show-owner {
		@media only screen and (min-width: 1201px) {
			grid-template-columns: 5% repeat(2, 1fr) repeat(3, 15%) 5%;
		}
	}
}
@media only screen and (max-width: 549px) {
	.table-body-row {
		grid-template-columns: 8% 1fr 44px;
	}

	.table-body__email,
	.table-body__type,
	.table-body__phone,
	.table-body__owner {
		display: none;
	}
}

@media only screen and (min-width: 550px) and (max-width: 659px) {
	.table-body-row {
		grid-template-columns: 5% repeat(2, 1fr) 44px;
	}

	.table-body__type,
	.table-body__phone,
	.table-body__owner {
		display: none;
	}
}

@media only screen and (min-width: 660px) and (max-width: 1049px) {
	.table-body-row {
		grid-template-columns: 5% repeat(3, 1fr) 44px;
	}

	.table-body__type,
	.table-body__owner {
		display: none;
	}
}

@media only screen and (min-width: 1050px) and (max-width: 1200px) {
	.table-body-row {
		grid-template-columns: 5% repeat(4, 1fr) 44px;
	}

	.table-body__owner {
		display: none;
	}
}

.contact-icon {
	@include rem('margin-right', 15px);
	@include rem('width', 44px);
}

.link {
	color: $brand-blue;
	font-weight: 500;
	text-decoration: none;
	cursor: pointer;
	&.disabled {
		color: $b360-color--gray__medium-plus;
		pointer-events: none;
	}
}

.table-body__type,
.table-body__owner {
	&.disabled {
		color: $b360-color--gray__medium-plus;
	}
}
</style>